import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Search, Loader2, MessageSquare, Send, Users, Pause, Play, Check, Clock, Filter, ChevronDown, ChevronUp, Eye, ListFilter, RotateCcw, Info, Undo2, XCircle, BarChart3, CheckCircle, RefreshCw, Link2 } from 'lucide-react';
import { API, norm } from '@/lib/helpers';
import { formatBrazilDate } from '@/lib/timezone';

import { Phone, User, UserRound } from 'lucide-react';

function RecipientGroup({ title, items, selectedIds, togglePerson, contactType, renderLabel, selectAll, deselectAll, getKey }) {
  const [collapsed, setCollapsed] = useState(true);
  const keyFn = getKey || ((p) => `${p.pessoa_id}__${contactType}`);
  const groupKeys = items.map(p => keyFn(p));
  const allSelected = groupKeys.length > 0 && groupKeys.every(k => selectedIds.includes(k));
  const icon = contactType === 'proprio' ? <Phone className="w-3.5 h-3.5 text-green-600" /> : contactType === 'pai' ? <User className="w-3.5 h-3.5 text-blue-600" /> : <UserRound className="w-3.5 h-3.5 text-pink-600" />;
  const bgColor = 'bg-white border-slate-200';
  return (
    <div className={`rounded-lg border ${bgColor} overflow-hidden mb-3`} data-testid={`group-${contactType}`}>
      <button onClick={() => setCollapsed(p => !p)} className="w-full flex items-center justify-between px-3 py-2 text-left">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-xs font-semibold text-slate-700">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-5 px-2 text-[10px] rounded" onClick={(e) => { e.stopPropagation(); allSelected ? deselectAll() : selectAll(); }}>{allSelected ? 'Limpar' : 'Todos'}</Button>
          {collapsed ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronUp className="w-3.5 h-3.5 text-slate-400" />}
        </div>
      </button>
      {!collapsed && (
        <div className="px-1 pb-1 space-y-0.5">
          {items.map(p => {
            const key = keyFn(p);
            return (
              <label key={key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/60 cursor-pointer text-sm">
                <input type="checkbox" checked={selectedIds.includes(key)} onChange={() => togglePerson(key)} className="rounded border-slate-300" />
                {p.send_status && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.send_status === 'enviado' ? 'bg-green-500' : p.send_status === 'erro' ? 'bg-red-500' : 'bg-slate-300'}`} />}
                <span className="truncate text-slate-700">{renderLabel(p)}</span>
                <span className="text-xs text-slate-400 ml-auto flex-shrink-0">{p.whatsapp}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}


const STATUS_MAP = {
  rascunho: { label: 'Rascunho', color: 'bg-slate-100 text-slate-600', icon: Edit },
  pronta: { label: 'Pronta', color: 'bg-blue-100 text-blue-700', icon: Check },
  enviando: { label: 'Enviando', color: 'bg-amber-100 text-amber-700', icon: Send },
  pausada: { label: 'Pausada', color: 'bg-orange-100 text-orange-700', icon: Pause },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700', icon: Check },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const FILTROS = [
  { key: 'todos', label: 'Todas', color: 'bg-green-100 text-green-600' },
  { key: 'rascunho', label: 'Rascunhos', color: 'bg-slate-200 text-slate-600' },
  { key: 'pronta', label: 'Prontas', color: 'bg-blue-100 text-blue-600' },
  { key: 'enviando', label: 'Enviando', color: 'bg-amber-100 text-amber-600' },
  { key: 'concluida', label: 'Concluídas', color: 'bg-green-100 text-green-600' },
  { key: 'cancelada', label: 'Canceladas', color: 'bg-red-100 text-red-600' },
];

const MESES = [
  { value: '1', label: 'Janeiro' }, { value: '2', label: 'Fevereiro' }, { value: '3', label: 'Março' },
  { value: '4', label: 'Abril' }, { value: '5', label: 'Maio' }, { value: '6', label: 'Junho' },
  { value: '7', label: 'Julho' }, { value: '8', label: 'Agosto' }, { value: '9', label: 'Setembro' },
  { value: '10', label: 'Outubro' }, { value: '11', label: 'Novembro' }, { value: '12', label: 'Dezembro' },
];

const VARIABLES = [
  { key: '{primeiro_nome}', label: 'Primeiro Nome' },
  { key: '{nome_completo}', label: 'Nome Completo' },
  { key: '{apelido}', label: 'Apelido', tooltip: 'Se a pessoa tiver apelido, usa o apelido. Caso contrário, usa o primeiro nome.' },
  { key: '{telefone}', label: 'Telefone' },
  { key: '{email}', label: 'E-mail' },
  { key: '{data_nascimento}', label: 'Data Nasc.' },
  { key: '{idade}', label: 'Idade' },
  { key: '{genero}', label: 'Gênero' },
  { key: '{nome_mae}', label: 'Nome da Mãe' },
  { key: '{nome_pai}', label: 'Nome do Pai' },
];

// Variable label map for display
const VAR_LABELS = {};
VARIABLES.forEach(v => { VAR_LABELS[v.key] = v.label; });

function templateToHtml(text) {
  const varRegex = /\{([^}]+)\}/g;
  const VAR_PLACEHOLDER = '\x00VAR';
  const vars = [];
  // 1. Extract variables first
  let temp = (text || '').replace(varRegex, (match) => {
    vars.push(match);
    return VAR_PLACEHOLDER + (vars.length - 1) + '\x00';
  });
  // 2. Convert WhatsApp formatting to HTML
  temp = temp.replace(/\*([^*\n\x00]+)\*/g, '<b>$1</b>');
  temp = temp.replace(/\_([^_\n\x00]+)\_/g, '<i>$1</i>');
  temp = temp.replace(/\~([^~\n\x00]+)\~/g, '<s>$1</s>');
  // 3. Handle bold/italic/strike wrapping variables: *{var}* pattern
  // Check if a variable is inside a bold/italic/strike tag by looking at surrounding markers
  temp = temp.replace(/\*(\x00VAR\d+\x00)\*/g, '<b>$1</b>');
  temp = temp.replace(/_(\x00VAR\d+\x00)_/g, '<i>$1</i>');
  temp = temp.replace(/~(\x00VAR\d+\x00)~/g, '<s>$1</s>');
  // 4. Re-insert variables as pills (detect if inside formatting tags)
  const tempForCheck = temp;
  temp = temp.replace(/\x00VAR(\d+)\x00/g, (match, idx, offset) => {
    const varKey = vars[parseInt(idx)];
    const label = VAR_LABELS[varKey] || varKey;
    const before = tempForCheck.substring(0, tempForCheck.indexOf(match));
    const isBold = (before.lastIndexOf('<b>') > before.lastIndexOf('</b>'));
    const isItalic = (before.lastIndexOf('<i>') > before.lastIndexOf('</i>'));
    const isStrike = (before.lastIndexOf('<s>') > before.lastIndexOf('</s>'));
    let extra = '';
    if (isBold) extra += 'font-weight:bold;';
    if (isItalic) extra += 'font-style:italic;';
    if (isStrike) extra += 'text-decoration:line-through;';
    return `<span contenteditable="false" data-var="${varKey}" style="display:inline-block;padding:1px 8px;margin:0 2px;border-radius:9999px;border:1px solid #86efac;background:#f0fdf4;color:#15803d;font-size:12px;line-height:20px;white-space:nowrap;vertical-align:baseline;cursor:default;user-select:all;${extra}">${label}</span>`;
  });
  return temp;
}

function htmlToTemplate(el) {
  let result = '';
  el.childNodes.forEach(n => {
    if (n.nodeType === 3) { result += n.textContent; }
    else if (n.nodeType === 1) {
      if (n.dataset && n.dataset.var) { result += n.dataset.var; }
      else if (n.tagName === 'BR') { result += '\n'; }
      else if (n.tagName === 'B' || n.tagName === 'STRONG') {
        const raw = htmlToTemplate(n);
        const inner = raw.trim();
        const leadSpace = raw.match(/^\s*/)[0];
        const trailSpace = raw.match(/\s*$/)[0];
        if (inner) result += leadSpace + '*' + inner + '*' + trailSpace;
      }
      else if (n.tagName === 'I' || n.tagName === 'EM') {
        const raw = htmlToTemplate(n);
        const inner = raw.trim();
        const leadSpace = raw.match(/^\s*/)[0];
        const trailSpace = raw.match(/\s*$/)[0];
        if (inner) result += leadSpace + '_' + inner + '_' + trailSpace;
      }
      else if (n.tagName === 'S' || n.tagName === 'STRIKE' || n.tagName === 'DEL') {
        const raw = htmlToTemplate(n);
        const inner = raw.trim();
        const leadSpace = raw.match(/^\s*/)[0];
        const trailSpace = raw.match(/\s*$/)[0];
        if (inner) result += leadSpace + '~' + inner + '~' + trailSpace;
      }
      else if (n.tagName === 'DIV' || n.tagName === 'P') {
        if (result && !result.endsWith('\n')) result += '\n';
        result += htmlToTemplate(n);
      } else { result += htmlToTemplate(n); }
    }
  });
  // Merge adjacent same markers: *word* *word2* → *word word2*
  result = result.replace(/\*([^*]+)\*\s*\*([^*]+)\*/g, '*$1 $2*');
  result = result.replace(/_([^_]+)_\s*_([^_]+)_/g, '_$1 $2_');
  result = result.replace(/~([^~]+)~\s*~([^~]+)~/g, '~$1 $2~');
  return result;
}

function RichMessageEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const isComposing = useRef(false);

  const syncFromEditor = useCallback(() => {
    if (!editorRef.current || isComposing.current) return;
    const raw = htmlToTemplate(editorRef.current);
    onChange(raw);
  }, [onChange]);

  useEffect(() => {
    if (!editorRef.current) return;
    const el = editorRef.current;
    // Only re-render if the source of truth (value) differs from what's in the editor
    const current = htmlToTemplate(el);
    if (current !== value) {
      const sel = window.getSelection();
      const hadFocus = document.activeElement === el;
      el.innerHTML = templateToHtml(value);
      if (hadFocus) el.focus();
    }
  }, [value]);

  return (
    <div
      ref={editorRef}
      contentEditable
      suppressContentEditableWarning
      data-testid="campaign-message-input"
      onInput={syncFromEditor}
      onCompositionStart={() => { isComposing.current = true; }}
      onCompositionEnd={() => { isComposing.current = false; syncFromEditor(); }}
      className="min-h-[120px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowY: 'auto', maxHeight: '200px' }}
    />
  );
}

export default function WhatsAppTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [filter, setFilter] = useState('todos');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showStatusHelp, setShowStatusHelp] = useState(false);
  const [showSendSettings, setShowSendSettings] = useState(false);
  const [sendingCampaignId, setSendingCampaignId] = useState(null);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, logs: [], status: null });
  const [loading, setLoading] = useState(true);
  // Form state
  const [form, setForm] = useState({ name: '', message_template: '' });
  const [sendSettings, setSendSettings] = useState({ delay_min: 4, delay_max: 12, pause_count_min: 15, pause_count_max: 25, pause_dur_min: 10, pause_dur_max: 25 });
  const [filters, setFilters] = useState({ sexo: 'todos', batizado: 'todos', idade_min: '', idade_max: '', aniversariantes_mes: '' });
  const [filteredPeople, setFilteredPeople] = useState({ proprio: [], pai: [], mae: [] });
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchName, setSearchName] = useState('');
  // Group contacts
  const [recipientSource, setRecipientSource] = useState('pessoas'); // 'pessoas' | 'grupo'
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  // Unified accumulated recipients (deduplicated by phone)
  const [accumulatedRecipients, setAccumulatedRecipients] = useState([]); // [{ phone, nome, source, id, contact_type }]
  // Associate phone modal
  const [assocModal, setAssocModal] = useState(null); // { phone, nome }
  const [assocSearch, setAssocSearch] = useState('');
  const [assocPessoas, setAssocPessoas] = useState([]);
  const [assocSaving, setAssocSaving] = useState(false);
  const [assocConfirm, setAssocConfirm] = useState(null); // { pessoaId, pessoaNome, pessoaWhatsapp }
  // Quick add pessoa from group
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddForm, setQuickAddForm] = useState({ nome_completo: '', genero: 'feminino', data_nascimento: '' });
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  // Invite modal
  const [inviteModal, setInviteModal] = useState(null); // { phone, nome }
  const [inviteMsg, setInviteMsg] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [cadastroSlug, setCadastroSlug] = useState('');

  useEffect(() => {
    fetchCampaigns();
    fetch(`${API}/settings`, { credentials: 'include' }).then(r => r.ok ? r.json() : {}).then(d => setCadastroSlug(d.slug_cadastro || '')).catch(() => {});
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/whatsapp/campaigns`, { credentials: 'include' });
      if (r.ok) setCampaigns(await r.json());
    } catch {} finally { setLoading(false); }
  };

  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const r = await fetch(`${API}/evo/groups`, { credentials: 'include' });
      if (r.ok) setGroups(await r.json());
      else toast.error('Erro ao buscar grupos. Verifique se o WhatsApp está conectado.');
    } catch { toast.error('Erro ao buscar grupos'); }
    setLoadingGroups(false);
  };

  const fetchGroupMembers = async (groupJid) => {
    setLoadingMembers(true);
    setSelectedGroup(groupJid);
    setGroupMembers([]);
    setSelectedGroupIds([]);
    try {
      const r = await fetch(`${API}/evo/groups/${encodeURIComponent(groupJid)}/members`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setGroupMembers(data.members || []);
        setSelectedGroupIds((data.members || []).map(m => m.phone));
      } else toast.error('Erro ao buscar membros');
    } catch { toast.error('Erro ao buscar membros'); }
    setLoadingMembers(false);
  };

  // Helper: normalize phone for deduplication
  const normalizePhone = (p) => (p || '').replace(/\D/g, '').replace(/^55/, '');

  // Add recipients from group to accumulated list
  const addGroupToAccumulated = () => {
    if (selectedGroupIds.length === 0) return;
    const groupName = groups.find(g => g.id === selectedGroup)?.subject || 'Grupo';
    setAccumulatedRecipients(prev => {
      const existing = new Set(prev.map(r => normalizePhone(r.phone)));
      const newOnes = selectedGroupIds
        .filter(phone => !existing.has(normalizePhone(phone)))
        .map(phone => {
          const member = groupMembers.find(m => m.phone === phone);
          return { phone, nome: member?.nome || phone, source: groupName, id: `grp_${phone}`, contact_type: 'grupo' };
        });
      return [...prev, ...newOnes];
    });
    toast.success(`${selectedGroupIds.length} contatos adicionados do grupo`);
    setSelectedGroupIds([]);
  };

  // Add recipients from pessoas to accumulated list
  const addPessoasToAccumulated = () => {
    if (selectedIds.length === 0) return;
    const all = [...(filteredPeople.proprio || []), ...(filteredPeople.pai || []), ...(filteredPeople.mae || [])];
    setAccumulatedRecipients(prev => {
      const existing = new Set(prev.map(r => normalizePhone(r.phone)));
      const newOnes = [];
      for (const rid of selectedIds) {
        let key, ctype;
        if (rid.includes('__')) { const parts = rid.split('__'); ctype = parts.pop(); key = parts.join('__'); }
        else { key = rid; ctype = 'proprio'; }
        let phone = '', nome = '';
        if (ctype === 'proprio') {
          const p = all.find(x => x.pessoa_id === key);
          if (p) { phone = p.whatsapp || ''; nome = p.nome_completo || ''; }
        } else if (ctype === 'pai') {
          phone = key; nome = `Pai (${key})`;
        } else if (ctype === 'mae') {
          phone = key; nome = `Mãe (${key})`;
        }
        if (phone && !existing.has(normalizePhone(phone))) {
          existing.add(normalizePhone(phone));
          newOnes.push({ phone, nome, source: 'Pessoas', id: rid, contact_type: ctype || 'proprio' });
        }
      }
      return [...prev, ...newOnes];
    });
    toast.success(`${selectedIds.length} contatos adicionados`);
    setSelectedIds([]);
  };

  const removeFromAccumulated = (phone) => {
    setAccumulatedRecipients(prev => prev.filter(r => r.phone !== phone));
  };

  const clearAccumulated = () => { setAccumulatedRecipients([]); };

  const openAssocModal = (phone, nome) => {
    setAssocModal({ phone, nome });
    setAssocSearch('');
    setAssocPessoas([]);
    setAssocConfirm(null);
    setQuickAddOpen(false);
    fetch(`${API}/pessoas?status=aprovado`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setAssocPessoas)
      .catch(() => {});
  };

  const openQuickAdd = () => {
    // Format phone for the form
    const ph = assocModal?.phone || '';
    let digits = ph.startsWith('55') && ph.length > 10 ? ph.slice(2) : ph;
    if (digits.length === 10) digits = digits.slice(0,2) + '9' + digits.slice(2);
    const formatted = digits.length === 11 ? `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}` : ph;
    setQuickAddForm({ nome_completo: assocModal?.nome || '', genero: '', data_nascimento: '' });
    setQuickAddOpen(true);
    // Store formatted phone for use
    setAssocModal(prev => ({ ...prev, formattedPhone: formatted }));
  };

  const handleQuickAdd = async () => {
    if (!quickAddForm.nome_completo.trim()) { toast.error('Nome é obrigatório'); return; }
    if (!quickAddForm.genero) { toast.error('Gênero é obrigatório'); return; }
    setQuickAddSaving(true);
    try {
      const r = await fetch(`${API}/pessoas`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({
          nome_completo: quickAddForm.nome_completo.trim(),
          genero: quickAddForm.genero,
          data_nascimento: quickAddForm.data_nascimento,
          whatsapp: assocModal?.formattedPhone || '',
          apelido: '', email: '', batizado: false, selado: false,
          nome_pai: '', nome_mae: '', whatsapp_pai: '', whatsapp_mae: '',
        }),
      });
      if (r.ok) {
        toast.success('Pessoa cadastrada e número associado!');
        setQuickAddOpen(false);
        setAssocModal(null);
        if (selectedGroup) fetchGroupMembers(selectedGroup);
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao cadastrar');
      }
    } catch { toast.error('Erro ao cadastrar'); }
    setQuickAddSaving(false);
  };

  const openInvite = (phone, nome) => {
    const link = cadastroSlug ? `${window.location.origin}/pessoas/${cadastroSlug}` : '[link do cadastro]';
    setInviteModal({ phone, nome });
    setInviteMsg(`Paz de Deus, belezinha aí? Estava vendo aqui e percebi que você ainda não preencheu nosso formulário. Geralmente usamos um sistema para envio de mensagens quando temos eventos, visitas, etc. Vou te enviar o link pra preencher. Quando conseguir preencher me avisa?\n\n${link}\n\nDeus te abençoe.`);
  };

  const handleSendInvite = async () => {
    if (!inviteModal || !inviteMsg.trim()) return;
    setInviteSending(true);
    try {
      let number = inviteModal.phone.replace(/\D/g, '');
      if (!number.startsWith('55')) number = '55' + number;
      const r = await fetch(`${API}/evo/send/text`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ number, text: inviteMsg }),
      });
      if (r.ok) { toast.success('Convite enviado!'); setInviteModal(null); }
      else { const err = await r.json().catch(() => ({})); toast.error(err.detail || 'Erro ao enviar'); }
    } catch { toast.error('Erro ao enviar'); }
    setInviteSending(false);
  };

  const handleAssociate = async () => {
    if (!assocModal || !assocConfirm) return;
    setAssocSaving(true);
    try {
      const r = await fetch(`${API}/evo/associate-phone`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ pessoa_id: assocConfirm.pessoaId, phone: assocModal.phone, field: 'whatsapp' }),
      });
      if (r.ok) {
        toast.success('Número associado com sucesso!');
        setAssocModal(null);
        setAssocConfirm(null);
        if (selectedGroup) fetchGroupMembers(selectedGroup);
      } else toast.error('Erro ao associar');
    } catch { toast.error('Erro ao associar'); }
    setAssocSaving(false);
  };


  const applyFilters = async () => {
    setLoadingPeople(true);
    try {
      const f = {};
      if (filters.sexo !== 'todos') f.sexo = filters.sexo;
      if (filters.batizado !== 'todos') f.batizado = filters.batizado;
      if (filters.aniversariantes_mes) f.aniversariantes_mes = filters.aniversariantes_mes;      if (filters.idade_min) f.idade_min = parseInt(filters.idade_min);
      if (filters.idade_max) f.idade_max = parseInt(filters.idade_max);
      if (filters.aniversariantes_mes) f.aniversariantes_mes = filters.aniversariantes_mes;
      const r = await fetch(`${API}/whatsapp/filter-recipients`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ filters: f })
      });
      if (r.ok) {
        const data = await r.json();
        setFilteredPeople(data);
        setSelectedIds([]);
      }
    } catch { toast.error('Erro ao filtrar'); } finally { setLoadingPeople(false); }
  };

  const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  const openAdd = async () => {
    // Check if WhatsApp Evolution is connected
    try {
      const statusR = await fetch(`${API}/evo/instance/status`, { credentials: 'include' });
      if (statusR.ok) {
        const statusData = await statusR.json();
        const state = statusData?.instance?.state || statusData?.state || '';
        if (state !== 'open') {
          toast.error('WhatsApp não está conectado. Ative em Menu > Configurações > Complementos > WhatsApp e conecte pelo QR Code antes de criar uma campanha.');
          return;
        }
      }
    } catch {
      toast.error('Não foi possível verificar o status do WhatsApp. Verifique a conexão.');
      return;
    }
    setEditing(null);
    setForm({ name: '', message_template: '' });
    setSendSettings({ delay_min: 4, delay_max: 12, pause_count_min: 15, pause_count_max: 25, pause_dur_min: 10, pause_dur_max: 25 });
    setFilters({ sexo: 'todos', batizado: 'todos', idade_min: '', idade_max: '', aniversariantes_mes: '' });
    setFilteredPeople({ proprio: [], pai: [], mae: [] });
    setSelectedIds([]);
    setShowFilters(false);
    setSearchName('');
    setDialog(true);
    // Auto-load all recipients
    try {
      const r = await fetch(`${API}/whatsapp/filter-recipients`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ filters: {} })
      });
      if (r.ok) {
        const data = await r.json();
        setFilteredPeople(data);
        setSelectedIds([]);
      }
    } catch {}
  };

  const openEdit = async (c) => {
    setEditing(c);
    setForm({ name: c.name, message_template: c.message_template });
    setSendSettings(c.settings || { delay_min: 4, delay_max: 12, pause_count_min: 15, pause_count_max: 25, pause_dur_min: 10, pause_dur_max: 25 });
    setFilters(c.filters || { sexo: 'todos', batizado: 'todos', idade_min: '', idade_max: '', aniversariantes_mes: '', atribuicao: 'todos' });
    setShowFilters(false);
    setSearchName('');
    // Load full campaign with recipients
    try {
      const r = await fetch(`${API}/whatsapp/campaigns/${c.campaign_id}`, { credentials: 'include' });
      if (r.ok) {
        const full = await r.json();
        const recs = (full.recipients || []);
        // Rebuild grouped format from saved recipients
        const proprio = recs.filter(r => (r.contact_type || 'proprio') === 'proprio').map(r => ({ pessoa_id: r.pessoa_id, nome_completo: r.nome, whatsapp: r.telefone, send_status: r.status, contact_type: 'proprio' }));
        const pai = recs.filter(r => r.contact_type === 'pai').map(r => ({ pessoa_id: r.pessoa_id, nome_completo: r.nome, whatsapp: r.telefone, send_status: r.status, contact_type: 'pai' }));
        const mae = recs.filter(r => r.contact_type === 'mae').map(r => ({ pessoa_id: r.pessoa_id, nome_completo: r.nome, whatsapp: r.telefone, send_status: r.status, contact_type: 'mae' }));
        setFilteredPeople({ proprio, pai, mae });
        setSelectedIds([]);
      }
    } catch {}
    setDialog(true);
  };

  const handleSave = async (asDraft = true) => {
    if (!form.name.trim()) { toast.error('Nome da campanha é obrigatório'); return; }
    if (!form.message_template.trim()) { toast.error('Mensagem é obrigatória'); return; }
    const hasRecipients = accumulatedRecipients.length > 0;
    if (!asDraft && !hasRecipients) { toast.error('Selecione pelo menos um destinatário'); return; }
    setSaving(true);
    try {
      // Split accumulated into pessoas and groups
      const pessoaIds = accumulatedRecipients.filter(r => r.contact_type !== 'grupo').map(r => r.id);
      const groupRecips = accumulatedRecipients.filter(r => r.contact_type === 'grupo').map(r => ({ phone: r.phone, nome: r.nome }));
      const payload = {
        name: form.name,
        message_template: form.message_template,
        filters,
        settings: sendSettings,
        recipient_ids: pessoaIds,
      };
      if (groupRecips.length > 0) {
        payload.group_recipients = groupRecips;
      }
      const url = editing ? `${API}/whatsapp/campaigns/${editing.campaign_id}` : `${API}/whatsapp/campaigns`;
      const r = await fetch(url, { method: editing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!r.ok) { let msg = 'Erro'; try { const e = await r.json(); msg = e.detail || msg; } catch {} throw new Error(msg); }
      const data = await r.json();

      if (!asDraft && data.campaign_id) {
        await fetch(`${API}/whatsapp/campaigns/${data.campaign_id || editing?.campaign_id}/ready`, { method: 'POST', credentials: 'include' });
      }

      toast.success(editing ? 'Campanha atualizada!' : 'Campanha criada!');
      setDialog(false);
      fetchCampaigns();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API}/whatsapp/campaigns/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('Campanha excluída!');
      setDeleteConfirm(null);
      fetchCampaigns();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleMarkReady = async (id) => {
    try {
      const r = await fetch(`${API}/whatsapp/campaigns/${id}/ready`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { let msg = 'Erro'; try { const e = await r.json(); msg = e.detail || msg; } catch {} throw new Error(msg); }
      toast.success('Campanha pronta para envio!');
      fetchCampaigns();
    } catch (err) { toast.error(err.message); }
  };

  const handlePause = async (id) => {
    try {
      await fetch(`${API}/whatsapp/campaigns/${id}/pause`, { method: 'POST', credentials: 'include' });
      toast.success('Campanha pausada');
      fetchCampaigns();
    } catch { toast.error('Erro ao pausar'); }
  };

  const handleResend = async (id) => {
    try {
      const r = await fetch(`${API}/whatsapp/campaigns/${id}/resend`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { let msg = 'Erro'; try { const e = await r.json(); msg = e.detail || msg; } catch {} throw new Error(msg); }
      toast.success('Campanha pronta para reenvio!');
      fetchCampaigns();
    } catch (err) { toast.error(err.message); }
  };

  const handleBackToDraft = async (id) => {
    try {
      const r = await fetch(`${API}/whatsapp/campaigns/${id}/back-to-draft`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { let msg = 'Erro'; try { const e = await r.json(); msg = e.detail || msg; } catch {} throw new Error(msg); }
      toast.success('Campanha voltou para rascunho');
      fetchCampaigns();
    } catch (err) { toast.error(err.message); }
  };

  const handleCancel = async (id) => {
    try {
      const r = await fetch(`${API}/whatsapp/campaigns/${id}/cancel`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { let msg = 'Erro'; try { const e = await r.json(); msg = e.detail || msg; } catch {} throw new Error(msg); }
      toast.success('Campanha cancelada');
      fetchCampaigns();
    } catch (err) { toast.error(err.message); }
  };

  const [sendConfirm, setSendConfirm] = useState(null); // { campaign with full recipients }
  const [sendConfirmLoading, setSendConfirmLoading] = useState(false);
  const [pauseConfirm, setPauseConfirm] = useState(false);
  const [actionConfirm, setActionConfirm] = useState(null); // { type, id, title, description }

  const openSendConfirm = async (c) => {
    setSendConfirmLoading(true);
    setSendConfirm(c);
    try {
      const r = await fetch(`${API}/whatsapp/campaigns/${c.campaign_id}`, { credentials: 'include' });
      if (r.ok) { const full = await r.json(); setSendConfirm(full); }
    } catch {}
    setSendConfirmLoading(false);
  };

  const handleSendEvo = async (campaignId, total) => {
    setSendingCampaignId(campaignId);
    setSendProgress({ sent: 0, total, logs: [], status: 'sending' });
    try {
      const r = await fetch(`${API}/whatsapp/campaigns/${campaignId}/send-evo`, { method: 'POST', credentials: 'include' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao iniciar envio');
        setSendProgress(prev => ({ ...prev, status: 'error' }));
        return;
      }
    } catch { toast.error('Erro ao iniciar envio'); setSendProgress(prev => ({ ...prev, status: 'error' })); return; }
    // Start polling
    const pollInterval = setInterval(async () => {
      try {
        const r = await fetch(`${API}/whatsapp/campaigns/${campaignId}/send-progress`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        setSendProgress(prev => ({
          sent: data.sent || 0,
          total: data.total || prev.total,
          logs: (data.log || []).map(l => l),
          status: data.status === 'concluida' ? 'complete' : data.status === 'pausada' ? 'cancelled' : data.status === 'enviando' ? 'sending' : prev.status,
        }));
        if (data.status === 'concluida' || data.status === 'pausada') {
          clearInterval(pollInterval);
          fetchCampaigns();
        }
      } catch {}
    }, 2000);
    // Store interval for cleanup
    setSendingCampaignId(prev => { if (prev) window._sendPollInterval = pollInterval; return campaignId; });
    window._sendPollInterval = pollInterval;
  };

  const handleCancelSend = async () => {
    if (!sendingCampaignId) return;
    try {
      await fetch(`${API}/whatsapp/campaigns/${sendingCampaignId}/cancel-send`, { method: 'POST', credentials: 'include' });
    } catch {}
    if (window._sendPollInterval) clearInterval(window._sendPollInterval);
  };

  const openDetail = async (c) => {
    try {
      const r = await fetch(`${API}/whatsapp/campaigns/${c.campaign_id}`, { credentials: 'include' });
      if (r.ok) setDetailDialog(await r.json());
    } catch { toast.error('Erro ao carregar detalhes'); }
  };

  const insertVariable = (varKey) => {
    const el = document.querySelector('[data-testid="campaign-message-input"]');
    if (el) {
      el.focus();
      const sel = window.getSelection();
      if (sel.rangeCount) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const span = document.createElement('span');
        span.contentEditable = 'false';
        span.dataset.var = varKey;
        span.style.cssText = 'display:inline-block;padding:1px 8px;margin:0 2px;border-radius:9999px;border:1px solid #86efac;background:#f0fdf4;color:#15803d;font-size:12px;line-height:20px;white-space:nowrap;vertical-align:baseline;cursor:default;user-select:all';
        span.textContent = VAR_LABELS[varKey] || varKey;
        range.insertNode(span);
        // Move cursor after the span
        range.setStartAfter(span);
        range.setEndAfter(span);
        sel.removeAllRanges();
        sel.addRange(range);
      }
      // Sync to form
      const raw = htmlToTemplate(el);
      setForm(prev => ({ ...prev, message_template: raw }));
    } else {
      setForm(prev => ({ ...prev, message_template: prev.message_template + varKey }));
    }
  };

  const wrapSelection = (type) => {
    const el = document.querySelector('[data-testid="campaign-message-input"]');
    if (!el) return;
    el.focus();
    const cmd = type === '*' ? 'bold' : type === '_' ? 'italic' : 'strikeThrough';
    document.execCommand(cmd, false, null);
    const raw = htmlToTemplate(el);
    setForm(prev => ({ ...prev, message_template: raw }));
  };

  const togglePerson = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // Search filter for grouped recipients
  const searchFilter = (items, contactType) => {
    if (!searchName) return items;
    const q = norm(searchName);
    const qDigits = searchName.replace(/\D/g, '');
    return items.filter(p => {
      const digits = (p.whatsapp || '').replace(/\D/g, '');
      if (qDigits && digits.includes(qDigits)) return true;
      if (norm(p.nome_completo || '').includes(q)) return true;
      if (contactType === 'pai' && norm(p.nome_pai || '').includes(q)) return true;
      if (contactType === 'mae' && norm(p.nome_mae || '').includes(q)) return true;
      if ((p.filhos || []).some(f => norm(f).includes(q))) return true;
      return false;
    });
  };

  const filteredProprio = searchFilter(filteredPeople.proprio || [], 'proprio');
  const filteredPai = searchFilter(filteredPeople.pai || [], 'pai');
  const filteredMae = searchFilter(filteredPeople.mae || [], 'mae');
  const totalRecipients = (filteredPeople.proprio || []).length + (filteredPeople.pai || []).length + (filteredPeople.mae || []).length;
  const allRecipientKeys = [
    ...(filteredPeople.proprio || []).map(p => `${p.pessoa_id}__proprio`),
    ...(filteredPeople.pai || []).map(p => `${p.whatsapp}__pai`),
    ...(filteredPeople.mae || []).map(p => `${p.whatsapp}__mae`),
  ];

  const counts = {
    todos: campaigns.length,
    rascunho: campaigns.filter(c => c.status === 'rascunho').length,
    pronta: campaigns.filter(c => c.status === 'pronta').length,
    enviando: campaigns.filter(c => c.status === 'enviando').length,
    concluida: campaigns.filter(c => c.status === 'concluida').length,
    cancelada: campaigns.filter(c => c.status === 'cancelada').length,
  };

  const filtered = campaigns.filter(c => {
    if (filter !== 'todos' && c.status !== filter) return false;
    if (search && !norm(c.name).includes(norm(search))) return false;
    return true;
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar */}
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>WhatsApp</h2>
                  <Badge className="bg-green-100 text-green-700 text-sm font-bold">{counts.todos}</Badge>
                </div>
                <p className="text-sm text-slate-500">Campanhas de mensagens</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="whatsapp-menu">
              {FILTROS.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filtro-whatsapp-${f.key}`}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-3 ${filter === f.key ? 'border-green-600 bg-green-50 shadow-sm' : 'border-transparent bg-slate-50 hover:bg-slate-100'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${f.color.split(' ')[0]}`}>
                      <ListFilter className={`w-4 h-4 ${f.color.split(' ')[1]}`} />
                    </div>
                    <p className={`font-semibold text-sm ${filter === f.key ? 'text-green-700' : 'text-slate-900'}`}>{f.label}</p>
                  </div>
                  <Badge className={`text-xs font-semibold ${filter === f.key ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{counts[f.key]}</Badge>
                </button>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              <Button size="sm" onClick={openAdd} className="w-full bg-green-600 hover:bg-green-700" data-testid="add-campaign-btn">
                <Plus className="w-4 h-4 mr-1" />Nova Campanha
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{FILTROS.find(f => f.key === filter)?.label}</h2>
                <p className="text-sm text-slate-500">{filtered.length} {filtered.length === 1 ? 'campanha' : 'campanhas'}</p>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-600 ml-auto" onClick={() => setShowStatusHelp(true)} data-testid="status-help-btn">
                      <Info className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Como funcionam os status</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" data-testid="whatsapp-search" />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-green-600" /></div>
            ) : (
              <div className="space-y-0" data-testid="campaigns-list">
                {filtered.map(c => {
                  const st = STATUS_MAP[c.status] || STATUS_MAP.rascunho;
                  const StIcon = st.icon;
                  const pct = c.total > 0 ? Math.round((c.sent / c.total) * 100) : 0;
                  return (
                    <div key={c.campaign_id} className="px-3 sm:px-4 py-3 border-b border-slate-100 last:border-b-0" data-testid={`campaign-${c.campaign_id}`}>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${c.status === 'concluida' ? 'bg-gradient-to-br from-green-500 to-green-600' : c.status === 'enviando' ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gradient-to-br from-slate-400 to-slate-500'}`}>
                          <StIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <p className="font-semibold text-sm text-slate-900 truncate">{c.name}</p>
                            <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-slate-600">
                            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.sent}/{c.total}</span>
                            {c.total > 0 && (
                              <span className="flex items-center gap-1.5">
                                <div className="w-12 sm:w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full bg-green-500" style={{ width: `${pct}%` }}></div>
                                </div>
                                <span className="font-semibold text-green-600">{pct}%</span>
                              </span>
                            )}
                            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {formatBrazilDate(c.created_at, '-')}</span>
                          </div>
                        </div>
                        <div className="flex gap-0.5 flex-shrink-0 items-center">
                          {/* RASCUNHO: Aprovar, Editar, Excluir */}
                          {c.status === 'rascunho' && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => setActionConfirm({ type: 'ready', id: c.campaign_id, title: 'Aprovar Campanha', description: 'Deseja aprovar esta campanha e marcá-la como pronta para envio?' })} title="Aprovar campanha" data-testid={`ready-campaign-${c.campaign_id}`}><Check className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(c)} title="Editar"><Edit className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirm(c.campaign_id)} title="Excluir"><Trash2 className="w-4 h-4" /></Button>
                            </>
                          )}
                          {/* PRONTA: Enviar, Voltar rascunho, Editar, Excluir */}
                          {c.status === 'pronta' && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => openSendConfirm(c)} title="Enviar via WhatsApp" data-testid={`send-evo-${c.campaign_id}`}><Send className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-600 hover:bg-slate-50" onClick={() => setActionConfirm({ type: 'draft', id: c.campaign_id, title: 'Voltar para Rascunho', description: 'Deseja voltar esta campanha para rascunho? Ela poderá ser editada novamente.' })} title="Voltar para rascunho"><Undo2 className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(c)} title="Editar"><Edit className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirm(c.campaign_id)} title="Excluir"><Trash2 className="w-4 h-4" /></Button>
                            </>
                          )}
                          {/* ENVIANDO: Pausar, Cancelar, Total enviados, Editar, Excluir */}
                          {c.status === 'enviando' && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-orange-600 hover:bg-orange-50" onClick={() => setActionConfirm({ type: 'pause', id: c.campaign_id, title: 'Pausar Envio', description: 'Deseja pausar o envio desta campanha? As mensagens já enviadas não serão afetadas e o envio poderá ser continuado depois.' })} title="Pausar envio"><Pause className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setActionConfirm({ type: 'cancel', id: c.campaign_id, title: 'Cancelar Envio', description: 'Deseja cancelar o envio desta campanha? As mensagens já enviadas não serão afetadas.' })} title="Cancelar envio"><XCircle className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" className="h-8 text-slate-600 hover:bg-slate-50 text-xs gap-1" onClick={() => openDetail(c)} title="Total de enviados"><BarChart3 className="w-3.5 h-3.5" /> {c.sent}/{c.total}</Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirm(c.campaign_id)} title="Excluir"><Trash2 className="w-4 h-4" /></Button>
                            </>
                          )}
                          {/* PAUSADA: Recomeçar, Cancelar, Total enviados, Excluir */}
                          {c.status === 'pausada' && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => setActionConfirm({ type: 'ready', id: c.campaign_id, title: 'Recomeçar Envio', description: 'Deseja recomeçar o envio desta campanha? Será retomado de onde parou.' })} title="Recomeçar envio"><Play className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setActionConfirm({ type: 'cancel', id: c.campaign_id, title: 'Cancelar Campanha', description: 'Deseja cancelar esta campanha? As mensagens já enviadas não serão afetadas.' })} title="Cancelar"><XCircle className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" className="h-8 text-slate-600 hover:bg-slate-50 text-xs gap-1" onClick={() => openDetail(c)} title="Total de enviados"><BarChart3 className="w-3.5 h-3.5" /> {c.sent}/{c.total}</Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirm(c.campaign_id)} title="Excluir"><Trash2 className="w-4 h-4" /></Button>
                            </>
                          )}
                          {/* CONCLUIDA: Reenviar, Total enviados, Excluir */}
                          {c.status === 'concluida' && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => setActionConfirm({ type: 'resend', id: c.campaign_id, title: 'Enviar Novamente', description: 'Deseja reenviar esta campanha? Todos os destinatários serão resetados para pendente e o envio recomeçará do início.' })} title="Enviar novamente"><RotateCcw className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" className="h-8 text-slate-600 hover:bg-slate-50 text-xs gap-1" onClick={() => openDetail(c)} title="Total de enviados"><BarChart3 className="w-3.5 h-3.5" /> {c.sent}/{c.total}</Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirm(c.campaign_id)} title="Excluir"><Trash2 className="w-4 h-4" /></Button>
                            </>
                          )}
                          {/* CANCELADA: Aprovar, Editar, Excluir */}
                          {c.status === 'cancelada' && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-50" onClick={() => setActionConfirm({ type: 'ready', id: c.campaign_id, title: 'Aprovar Campanha', description: 'Deseja aprovar esta campanha e marcá-la como pronta para envio?' })} title="Aprovar campanha"><Check className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(c)} title="Editar"><Edit className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirm(c.campaign_id)} title="Excluir"><Trash2 className="w-4 h-4" /></Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhuma campanha {filter !== 'todos' ? 'neste status' : ''}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={dialog} onOpenChange={(o) => { if (!o) setDialog(false); }}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Editar Campanha' : 'Nova Campanha'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* LEFT COLUMN - Filters & Recipients */}
            <div className="space-y-4">
              {/* Source selector */}
              <div className="flex bg-slate-100 rounded-lg p-0.5">
                <button onClick={() => { setRecipientSource('pessoas'); setGroupMembers([]); setSelectedGroup(null); }}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${recipientSource === 'pessoas' ? 'bg-white shadow-sm text-green-700' : 'text-slate-600 hover:text-slate-900'}`}
                  data-testid="source-pessoas">
                  <Users className="w-3.5 h-3.5 inline mr-1.5" />Pessoas
                </button>
                <button onClick={() => { setRecipientSource('grupo'); if (groups.length === 0) fetchGroups(); }}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${recipientSource === 'grupo' ? 'bg-white shadow-sm text-green-700' : 'text-slate-600 hover:text-slate-900'}`}
                  data-testid="source-grupo">
                  <MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />Contatos de Grupo
                </button>
              </div>

              {recipientSource === 'pessoas' ? (
              <>
              {/* Recipient Filters */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-3 mt-2">
                <button type="button" className="flex items-center justify-between w-full" onClick={() => setShowFilters(!showFilters)}>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" /> Filtros de Destinatários</p>
                  {showFilters ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
                {showFilters && (
                  <div className="space-y-3 pt-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Gênero</Label>
                        <Select value={filters.sexo} onValueChange={(v) => setFilters({ ...filters, sexo: v })}>
                          <SelectTrigger data-testid="filter-sexo"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            <SelectItem value="masculino">Masculino</SelectItem>
                            <SelectItem value="feminino">Feminino</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Batizado</Label>
                        <Select value={filters.batizado} onValueChange={(v) => setFilters({ ...filters, batizado: v })}>
                          <SelectTrigger data-testid="filter-batizado"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            <SelectItem value="sim">Sim</SelectItem>
                            <SelectItem value="nao">Não</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Idade mínima</Label>
                        <Input type="number" min="0" value={filters.idade_min} onChange={(e) => setFilters({ ...filters, idade_min: e.target.value })} data-testid="filter-idade-min" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Idade máxima</Label>
                        <Input type="number" min="0" value={filters.idade_max} onChange={(e) => setFilters({ ...filters, idade_max: e.target.value })} data-testid="filter-idade-max" />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Aniversariantes</Label>
                        <Select value={filters.aniversariantes_mes || 'none'} onValueChange={(v) => setFilters({ ...filters, aniversariantes_mes: v === 'none' ? '' : v })}>
                          <SelectTrigger data-testid="filter-aniversariantes"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Todos os meses</SelectItem>
                            {MESES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <Button size="sm" onClick={applyFilters} className="bg-green-600 hover:bg-green-700" disabled={loadingPeople} data-testid="apply-filters-btn">
                      {loadingPeople ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Filter className="w-4 h-4 mr-1" />}
                      Buscar Destinatários
                    </Button>
                  </div>
                )}
              </div>

              {/* Recipients Preview */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-3 mt-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> Destinatários
                    {totalRecipients > 0 && <Badge className="bg-green-100 text-green-700 text-xs ml-1">{selectedIds.length}/{totalRecipients}</Badge>}
                  </p>
                  {totalRecipients > 0 && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-5 px-2 text-[10px] rounded" onClick={() => setSelectedIds(allRecipientKeys)} data-testid="select-all-recipients">Todos</Button>
                      <Button size="sm" variant="outline" className="h-5 px-2 text-[10px] rounded" onClick={() => setSelectedIds([])} data-testid="clear-all-recipients">Limpar</Button>
                    </div>
                  )}
                </div>
                {totalRecipients > 0 ? (
                  <div className="mt-3">
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                      <Input value={searchName} onChange={(e) => setSearchName(e.target.value)} placeholder="Buscar por nome, pai, mãe ou número..." className="pl-8 h-8 text-sm bg-white" data-testid="search-recipient-name" />
                    </div>
                    <div className="max-h-[340px] overflow-y-auto mt-3">
                      <div className="space-y-2">
                      {/* WhatsApp Próprio */}
                      {filteredProprio.length > 0 && (
                        <RecipientGroup
                          title={`WhatsApp Próprio (${filteredProprio.length})`}
                          items={filteredProprio}
                          selectedIds={selectedIds}
                          togglePerson={togglePerson}
                          contactType="proprio"
                          renderLabel={(p) => p.nome_completo}
                          selectAll={() => { const keys = filteredProprio.map(p => `${p.pessoa_id}__proprio`); setSelectedIds(prev => [...new Set([...prev, ...keys])]); }}
                          deselectAll={() => { const keys = new Set(filteredProprio.map(p => `${p.pessoa_id}__proprio`)); setSelectedIds(prev => prev.filter(k => !keys.has(k))); }}
                        />
                      )}
                      {/* WhatsApp do Pai */}
                      {filteredPai.length > 0 && (
                        <RecipientGroup
                          title={`WhatsApp do Pai (${filteredPai.length})`}
                          items={filteredPai}
                          selectedIds={selectedIds}
                          togglePerson={togglePerson}
                          contactType="pai"
                          renderLabel={(p) => {
                            const filhos = (p.filhos || []).join(', ');
                            return `${p.nome_pai || 'Pai'} (pai de ${filhos})`;
                          }}
                          getKey={(p) => `${p.whatsapp}__pai`}
                          selectAll={() => { const keys = filteredPai.map(p => `${p.whatsapp}__pai`); setSelectedIds(prev => [...new Set([...prev, ...keys])]); }}
                          deselectAll={() => { const keys = new Set(filteredPai.map(p => `${p.whatsapp}__pai`)); setSelectedIds(prev => prev.filter(k => !keys.has(k))); }}
                        />
                      )}
                      {/* WhatsApp da Mãe */}
                      {filteredMae.length > 0 && (
                        <RecipientGroup
                          title={`WhatsApp da Mãe (${filteredMae.length})`}
                          items={filteredMae}
                          selectedIds={selectedIds}
                          togglePerson={togglePerson}
                          contactType="mae"
                          renderLabel={(p) => {
                            const filhos = (p.filhos || []).join(', ');
                            return `${p.nome_mae || 'Mãe'} (mãe de ${filhos})`;
                          }}
                          getKey={(p) => `${p.whatsapp}__mae`}
                          selectAll={() => { const keys = filteredMae.map(p => `${p.whatsapp}__mae`); setSelectedIds(prev => [...new Set([...prev, ...keys])]); }}
                          deselectAll={() => { const keys = new Set(filteredMae.map(p => `${p.whatsapp}__mae`)); setSelectedIds(prev => prev.filter(k => !keys.has(k))); }}
                        />
                      )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">Use os filtros acima para buscar destinatários</p>
                )}
                {selectedIds.length > 0 && (
                  <Button size="sm" className="w-full mt-2 bg-green-600 hover:bg-green-700 text-xs" onClick={addPessoasToAccumulated}>
                    Adicionar {selectedIds.length} ao envio
                  </Button>
                )}
              </div>
              </>
              ) : (
              <>
              {/* GROUP CONTACTS */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-3 mt-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" /> Selecionar Grupo
                  </p>
                  <Button size="sm" variant="ghost" onClick={fetchGroups} disabled={loadingGroups} className="h-6 px-2 text-[10px] text-slate-500">
                    <RefreshCw className={`w-3 h-3 mr-1 ${loadingGroups ? 'animate-spin' : ''}`} />Atualizar
                  </Button>
                </div>
                {loadingGroups ? (
                  <div className="flex items-center gap-2 py-4 justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /><span className="text-sm text-slate-400">Carregando grupos...</span></div>
                ) : groups.length > 0 ? (
                  <>
                    <div className="relative mt-2 mb-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                      <Input value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Buscar grupo..." className="pl-8 h-8 text-sm bg-white" data-testid="search-group" />
                    </div>
                    <div className="max-h-[200px] overflow-y-auto space-y-1">
                      {groups.filter(g => !groupSearch || (g.subject || '').toLowerCase().includes(groupSearch.toLowerCase())).map(g => {
                        const jid = g.id || '';
                        const name = g.subject || jid;
                        const isSelected = selectedGroup === jid;
                        return (
                          <button key={jid} onClick={() => fetchGroupMembers(jid)}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${isSelected ? 'bg-green-50 border border-green-300 text-green-800 font-medium' : 'bg-slate-50 hover:bg-slate-100 text-slate-700'}`}
                            data-testid={`group-${jid}`}>
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">Nenhum grupo encontrado. Verifique se o WhatsApp está conectado.</p>
                )}
              </div>
              {/* Group Members - separate card */}
              {selectedGroup && (
              <div className="border border-slate-200 rounded-lg p-4 space-y-3 mt-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" /> Membros
                        {groupMembers.length > 0 && <Badge className="bg-green-100 text-green-700 text-xs ml-1">{selectedGroupIds.length}/{groupMembers.length}</Badge>}
                      </p>
                      {groupMembers.length > 0 && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="h-5 px-2 text-[10px] rounded" onClick={() => setSelectedGroupIds(groupMembers.map(m => m.phone))}>Todos</Button>
                          <Button size="sm" variant="outline" className="h-5 px-2 text-[10px] rounded" onClick={() => setSelectedGroupIds(groupMembers.filter(m => m.source === 'cadastrado').map(m => m.phone))}>Cadastrados</Button>
                          <Button size="sm" variant="outline" className="h-5 px-2 text-[10px] rounded" onClick={() => setSelectedGroupIds([])}>Limpar</Button>
                        </div>
                      )}
                    </div>
                    {loadingMembers ? (
                      <div className="flex items-center gap-2 py-4 justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
                    ) : groupMembers.length > 0 ? (
                      <>
                        <div className="relative mt-2 mb-2">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                          <Input value={searchName} onChange={e => setSearchName(e.target.value)} placeholder="Buscar destinatário..." className="pl-8 h-8 text-sm bg-white" data-testid="search-group-member" />
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                          {groupMembers.filter(m => !searchName || (m.nome || m.phone).toLowerCase().includes(searchName.toLowerCase())).map(m => (
                            <label key={m.phone} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer text-xs border-b border-slate-50 last:border-b-0">
                              <input type="checkbox" checked={selectedGroupIds.includes(m.phone)} onChange={() => setSelectedGroupIds(prev => prev.includes(m.phone) ? prev.filter(p => p !== m.phone) : [...prev, m.phone])} className="rounded border-slate-300 w-3.5 h-3.5" />
                              <span className="text-slate-800 font-medium truncate flex-1">{m.nome || `+${m.phone}`}</span>
                              {m.source === 'cadastrado' && <Badge className="bg-green-100 text-green-700 text-[8px] flex-shrink-0 gap-0.5"><CheckCircle className="w-2.5 h-2.5" />Cadastrado</Badge>}
                              {m.source !== 'cadastrado' && (
                                <Badge className="bg-slate-100 text-slate-500 text-[8px] flex-shrink-0 gap-0.5 cursor-pointer hover:bg-blue-50 hover:text-blue-600" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openAssocModal(m.phone, m.nome); }} data-testid={`assoc-${m.phone}`}><Link2 className="w-2.5 h-2.5" />Associar</Badge>
                              )}
                              <span className="text-[10px] text-slate-400 flex-shrink-0 tabular-nums">{(() => { let d = m.phone.startsWith('55') && m.phone.length > 10 ? m.phone.slice(2) : m.phone; if (d.length === 10) d = d.slice(0,2) + '9' + d.slice(2); return d.length === 11 ? `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}` : `+${m.phone}`; })()}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-2">Nenhum membro encontrado</p>
                    )}
                    {selectedGroupIds.length > 0 && (
                      <Button size="sm" className="w-full mt-2 bg-green-600 hover:bg-green-700 text-xs" onClick={addGroupToAccumulated}>
                        Adicionar {selectedGroupIds.length} ao envio
                      </Button>
                    )}
              </div>
              )}
              </>
              )}

              {/* Unified accumulated recipients card */}
              <div className="border border-slate-200 rounded-lg p-4 space-y-2 mt-2" data-testid="accumulated-recipients">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> Destinatários Selecionados
                    <Badge className="bg-green-100 text-green-700 text-xs ml-1">{accumulatedRecipients.length}</Badge>
                  </p>
                  {accumulatedRecipients.length > 0 && (
                    <Button size="sm" variant="ghost" className="h-5 px-2 text-[10px] text-red-500 hover:text-red-700" onClick={clearAccumulated}>Limpar</Button>
                  )}
                </div>
                {accumulatedRecipients.length > 0 ? (
                  <div className="max-h-[150px] overflow-y-auto">
                    {accumulatedRecipients.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-0.5 text-[11px] text-slate-600 border-b border-slate-50 last:border-b-0">
                        <span className="flex-1 truncate">{r.nome}</span>
                        <Badge className="bg-slate-100 text-slate-400 text-[7px] px-1">{r.source}</Badge>
                        <button onClick={() => removeFromAccumulated(r.phone)} className="text-slate-300 hover:text-red-500 text-xs">&times;</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-slate-400 text-center py-2">Selecione destinatários em Pessoas ou Contatos de Grupo</p>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Name, Message, Settings */}
            <div className="space-y-4">
              <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Campanha</p>
                <div>
                  <Label className="text-xs text-slate-500">Nome da campanha *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-name-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs text-slate-500">Mensagem *</Label>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => wrapSelection('*')} title="Negrito *texto*" data-testid="fmt-bold"
                        className="w-7 h-7 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-700 transition-colors">B</button>
                      <button type="button" onClick={() => wrapSelection('_')} title="Itálico _texto_" data-testid="fmt-italic"
                        className="w-7 h-7 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-xs italic text-slate-700 transition-colors">I</button>
                      <button type="button" onClick={() => wrapSelection('~')} title="Riscado ~texto~" data-testid="fmt-strike"
                        className="w-7 h-7 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-xs line-through text-slate-700 transition-colors">S</button>
                    </div>
                  </div>
                  <RichMessageEditor value={form.message_template} onChange={(val) => setForm(prev => ({ ...prev, message_template: val }))} />
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {recipientSource === 'grupo' ? (
                      <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-full">
                        <Info className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>Variáveis não disponíveis para contatos de grupo (sem dados cadastrais).</span>
                      </div>
                    ) : VARIABLES.map(v => (
                      v.tooltip ? (
                        <TooltipProvider key={v.key}><Tooltip><TooltipTrigger asChild>
                          <button type="button" onClick={() => insertVariable(v.key)}
                            className="text-[11px] px-2 py-1 rounded-full border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors" data-testid={`var-${v.key}`}>
                            {v.label}
                          </button>
                        </TooltipTrigger><TooltipContent><p className="text-xs">{v.tooltip}</p></TooltipContent></Tooltip></TooltipProvider>
                      ) : (
                        <button key={v.key} type="button" onClick={() => insertVariable(v.key)}
                          className="text-[11px] px-2 py-1 rounded-full border border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors" data-testid={`var-${v.key}`}>
                          {v.label}
                        </button>
                      )
                    ))}
                  </div>
                </div>
              </div>
              {/* Timing settings - collapsible */}
              <div className="border border-slate-200 rounded-lg">
                <button type="button" onClick={() => setShowSendSettings(prev => !prev)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors" data-testid="toggle-send-settings">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Configurações de Envio</p>
                  {showSendSettings ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>
                {showSendSettings && (
                  <div className="px-4 pb-4 space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500">Intervalo entre mensagens (segundos)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">De</span>
                        <Input type="number" min="1" className="w-20" value={sendSettings.delay_min} onChange={(e) => setSendSettings({ ...sendSettings, delay_min: parseInt(e.target.value) || 1 })} data-testid="setting-delay-min" />
                        <span className="text-xs text-slate-400">a</span>
                        <Input type="number" min="1" className="w-20" value={sendSettings.delay_max} onChange={(e) => setSendSettings({ ...sendSettings, delay_max: parseInt(e.target.value) || 1 })} data-testid="setting-delay-max" />
                        <span className="text-xs text-slate-400">segundos</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Pausar a cada (mensagens)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">De</span>
                        <Input type="number" min="1" className="w-20" value={sendSettings.pause_count_min} onChange={(e) => setSendSettings({ ...sendSettings, pause_count_min: parseInt(e.target.value) || 1 })} data-testid="setting-pause-min" />
                        <span className="text-xs text-slate-400">a</span>
                        <Input type="number" min="1" className="w-20" value={sendSettings.pause_count_max} onChange={(e) => setSendSettings({ ...sendSettings, pause_count_max: parseInt(e.target.value) || 1 })} data-testid="setting-pause-max" />
                        <span className="text-xs text-slate-400">mensagens</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Duração da pausa (segundos)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400">De</span>
                        <Input type="number" min="1" className="w-20" value={sendSettings.pause_dur_min} onChange={(e) => setSendSettings({ ...sendSettings, pause_dur_min: parseInt(e.target.value) || 1 })} data-testid="setting-dur-min" />
                        <span className="text-xs text-slate-400">a</span>
                        <Input type="number" min="1" className="w-20" value={sendSettings.pause_dur_max} onChange={(e) => setSendSettings({ ...sendSettings, pause_dur_max: parseInt(e.target.value) || 1 })} data-testid="setting-dur-max" />
                        <span className="text-xs text-slate-400">segundos</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button onClick={() => handleSave(true)} disabled={saving} variant="outline" className="flex-1" data-testid="save-draft-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Edit className="w-4 h-4 mr-1" />}
                  Salvar Rascunho
                </Button>
                <Button onClick={() => handleSave(false)} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="save-ready-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Salvar e Preparar
                </Button>
              </div>
            </div>

          </div>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={!!detailDialog} onOpenChange={(o) => { if (!o) setDetailDialog(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0 sm:p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 flex-shrink-0">
            <DialogTitle className="text-base flex items-center gap-2"><BarChart3 className="w-5 h-5 text-slate-600" />Detalhes da Campanha</DialogTitle>
          </DialogHeader>
          {detailDialog && (() => {
            const d = detailDialog;
            const recips = d.recipients || [];
            const enviados = recips.filter(r => r.status === 'enviado');
            const erros = recips.filter(r => r.status === 'erro');
            const pendentes = recips.filter(r => r.status === 'pendente');
            const proprio = recips.filter(r => r.contact_type !== 'pai' && r.contact_type !== 'mae' && r.contact_type !== 'grupo');
            const pai = recips.filter(r => r.contact_type === 'pai');
            const mae = recips.filter(r => r.contact_type === 'mae');
            const grupo = recips.filter(r => r.contact_type === 'grupo');
            const pct = d.total > 0 ? Math.round((d.sent / d.total) * 100) : 0;
            return (
              <>
                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
                  {/* Stats cards */}
                  <div className="flex gap-2">
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-lg font-bold text-slate-700">{d.total}</p>
                      <p className="text-[10px] text-slate-500">Total</p>
                    </div>
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-lg font-bold text-slate-700">{d.sent}</p>
                      <p className="text-[10px] text-slate-500">Enviados</p>
                    </div>
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-lg font-bold text-slate-700">{pendentes.length}</p>
                      <p className="text-[10px] text-slate-500">Pendentes</p>
                    </div>
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-lg font-bold text-slate-700">{erros.length}</p>
                      <p className="text-[10px] text-slate-500">Erros</p>
                    </div>
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-lg font-bold text-slate-700">{pct}%</p>
                      <p className="text-[10px] text-slate-500">Concluído</p>
                    </div>
                  </div>

                  {/* Type breakdown */}
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Destinatários por tipo</p>
                  <div className="flex gap-2">
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-lg font-bold text-slate-700">{proprio.length}</p>
                      <p className="text-[10px] text-slate-500">Própria pessoa</p>
                    </div>
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-lg font-bold text-slate-700">{pai.length}</p>
                      <p className="text-[10px] text-slate-500">WhatsApp do Pai</p>
                    </div>
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-lg font-bold text-slate-700">{mae.length}</p>
                      <p className="text-[10px] text-slate-500">WhatsApp da Mãe</p>
                    </div>
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-lg font-bold text-slate-700">{grupo.length}</p>
                      <p className="text-[10px] text-slate-500">Contatos de Grupo</p>
                    </div>
                  </div>

                  {/* Recipients list */}
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider my-2">Lista de Destinatários</p>
                  <div className="max-h-[200px] overflow-y-auto border border-slate-200 rounded-lg p-2">
                    {recips.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1 border-b border-slate-50 last:border-b-0 text-[11px] text-slate-600">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.status === 'enviado' ? 'bg-green-500' : r.status === 'erro' ? 'bg-red-400' : 'bg-slate-300'}`} />
                        <span className="flex-1 truncate">{r.nome}</span>
                        <span className="text-slate-400 flex-shrink-0">{r.contact_type === 'pai' ? 'Pai' : r.contact_type === 'mae' ? 'Mãe' : r.contact_type === 'grupo' ? 'Grupo' : 'Próprio'}</span>
                        <span className="text-slate-400 tabular-nums flex-shrink-0">{r.telefone}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-slate-100 flex justify-end flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setDetailDialog(null)}>Fechar</Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modal Confirmação de Exclusão */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir campanha</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(deleteConfirm)}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Legenda dos Status */}
      <Dialog open={showStatusHelp} onOpenChange={setShowStatusHelp}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Como funcionam os status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex items-start gap-3">
              <Badge className="bg-slate-100 text-slate-700 mt-0.5 flex-shrink-0">Rascunho</Badge>
              <p className="text-sm text-slate-600">Campanha criada mas ainda incompleta. Pode ser editada livremente. Precisa ser marcada como "Pronta" para ser enviada.</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-100 text-blue-700 mt-0.5 flex-shrink-0">Pronta</Badge>
              <p className="text-sm text-slate-600">Campanha pronta para envio via Evolution API. Clique no botão enviar para disparar.</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-amber-100 text-amber-700 mt-0.5 flex-shrink-0">Enviando</Badge>
              <p className="text-sm text-slate-600">Envio em andamento via Evolution API. Pode ser cancelado.</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-orange-100 text-orange-700 mt-0.5 flex-shrink-0">Pausada</Badge>
              <p className="text-sm text-slate-600">Envio interrompido manualmente. Pode ser retomada marcando como "Pronta" novamente.</p>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-green-100 text-green-700 mt-0.5 flex-shrink-0">Concluida</Badge>
              <p className="text-sm text-slate-600">Todas as mensagens foram enviadas. Pode ser reenviada clicando no botao de envio.</p>
            </div>
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs font-semibold text-slate-700 mb-2">Fluxo dos botoes:</p>
              <div className="space-y-1 text-xs text-slate-500">
                <p><Send className="w-3 h-3 inline text-green-600" /> Marcar como pronta / Reenviar</p>
                <p><Edit className="w-3 h-3 inline text-blue-600" /> Editar campanha</p>
                <p><Pause className="w-3 h-3 inline text-orange-600" /> Pausar envio</p>
                <p><Play className="w-3 h-3 inline text-green-600" /> Retomar envio</p>
                <p><Trash2 className="w-3 h-3 inline text-red-600" /> Excluir campanha</p>
                <p><Eye className="w-3 h-3 inline text-slate-600" /> Ver detalhes e destinatarios</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmação de Pausa (durante envio) */}
      <AlertDialog open={pauseConfirm} onOpenChange={setPauseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Pausar Envio</AlertDialogTitle>
            <AlertDialogDescription>Deseja pausar o envio da campanha? As mensagens já enviadas não serão afetadas e o envio poderá ser continuado depois.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar Enviando</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setPauseConfirm(false); handleCancelSend(); }} className="bg-amber-600 hover:bg-amber-700">Pausar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Confirmação de Ação Genérica */}
      <AlertDialog open={!!actionConfirm} onOpenChange={(v) => { if (!v) setActionConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{actionConfirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{actionConfirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              const { type, id } = actionConfirm || {};
              setActionConfirm(null);
              if (type === 'ready') handleMarkReady(id);
              else if (type === 'pause') handlePause(id);
              else if (type === 'cancel') handleCancel(id);
              else if (type === 'draft') handleBackToDraft(id);
              else if (type === 'resend') handleResend(id);
            }} className={actionConfirm?.type === 'cancel' ? 'bg-red-600 hover:bg-red-700' : actionConfirm?.type === 'pause' ? 'bg-amber-600 hover:bg-amber-700' : ''}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Confirmação de Envio */}
      <Dialog open={!!sendConfirm} onOpenChange={(v) => { if (!v) setSendConfirm(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0 sm:p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 flex-shrink-0">
            <DialogTitle className="text-base flex items-center gap-2"><Send className="w-5 h-5 text-green-600" />Confirmar Envio</DialogTitle>
          </DialogHeader>
          {sendConfirm && (() => {
            const c = sendConfirm;
            const recips = c.recipients || [];
            const proprio = recips.filter(r => r.contact_type !== 'pai' && r.contact_type !== 'mae' && r.contact_type !== 'grupo');
            const pai = recips.filter(r => r.contact_type === 'pai');
            const mae = recips.filter(r => r.contact_type === 'mae');
            const grupo = recips.filter(r => r.contact_type === 'grupo');
            const settings = c.settings || {};
            return (
              <>
                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
                  {sendConfirmLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                  ) : (
                  <>
                  {/* Recipients summary */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Destinatários ({recips.length} total)</p>
                    <div className="flex gap-2 mt-2">
                      {proprio.length > 0 && (
                        <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                          <p className="text-lg font-bold text-slate-700">{proprio.length}</p>
                          <p className="text-[10px] text-slate-500">Própria pessoa</p>
                        </div>
                      )}
                      {pai.length > 0 && (
                        <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                          <p className="text-lg font-bold text-slate-700">{pai.length}</p>
                          <p className="text-[10px] text-slate-500">WhatsApp do Pai</p>
                        </div>
                      )}
                      {mae.length > 0 && (
                        <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                          <p className="text-lg font-bold text-slate-700">{mae.length}</p>
                          <p className="text-[10px] text-slate-500">WhatsApp da Mãe</p>
                        </div>
                      )}
                      {grupo.length > 0 && (
                        <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                          <p className="text-lg font-bold text-slate-700">{grupo.length}</p>
                          <p className="text-[10px] text-slate-500">Contatos de Grupo</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Send settings - inline cards */}
                  <div className="flex gap-2 mt-2">
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-[10px] text-slate-500">Intervalo</p>
                      <p className="text-sm font-bold text-slate-700">{settings.delay_min || 4}-{settings.delay_max || 12}s</p>
                    </div>
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-[10px] text-slate-500">Pausar a cada</p>
                      <p className="text-sm font-bold text-slate-700">{settings.pause_count_min || 15}-{settings.pause_count_max || 25} msgs</p>
                    </div>
                    <div className="flex-1 rounded-lg px-3 py-2" style={{ border: '1px dashed #cbd5e1' }}>
                      <p className="text-[10px] text-slate-500">Duração pausa</p>
                      <p className="text-sm font-bold text-slate-700">{settings.pause_dur_min || 10}-{settings.pause_dur_max || 25}s</p>
                    </div>
                  </div>

                  {/* Recipients list */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider my-2">Lista de Destinatários</p>
                    <div className="max-h-[200px] overflow-y-auto border border-slate-200 rounded-lg p-2">
                      {recips.map((r, i) => (
                        <div key={i} className="flex items-center gap-2 px-2 py-1 border-b border-slate-50 last:border-b-0 text-[11px] text-slate-600">
                          <span className="flex-1 truncate">{r.nome}</span>
                          <span className="text-slate-400 flex-shrink-0">{r.contact_type === 'pai' ? 'Pai' : r.contact_type === 'mae' ? 'Mãe' : r.contact_type === 'grupo' ? 'Grupo' : 'Próprio'}</span>
                          <span className="text-slate-400 tabular-nums flex-shrink-0">{r.telefone}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  </>
                  )}
                </div>
                <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setSendConfirm(null)}>Cancelar</Button>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1.5" onClick={() => { const id = c.campaign_id; const total = c.total; setSendConfirm(null); handleSendEvo(id, total); }}>
                    <Send className="w-4 h-4" />Confirmar Envio ({recips.length})
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modal Envio em Progresso (SSE) */}
      <Dialog open={!!sendingCampaignId} onOpenChange={(v) => { if (!v && sendProgress.status !== 'sending') { setSendingCampaignId(null); setSendProgress({ sent: 0, total: 0, logs: [], status: null }); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0 sm:p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100 flex-shrink-0">
            <DialogTitle className="text-base flex items-center gap-2">
              {sendProgress.status === 'complete' ? <CheckCircle className="w-5 h-5 text-green-600" /> : sendProgress.status === 'cancelled' ? <XCircle className="w-5 h-5 text-amber-600" /> : sendProgress.status === 'error' ? <XCircle className="w-5 h-5 text-red-600" /> : <Send className="w-5 h-5 text-green-600 animate-pulse" />}
              {sendProgress.status === 'complete' ? 'Envio Concluído' : sendProgress.status === 'cancelled' ? 'Envio Cancelado' : sendProgress.status === 'error' ? 'Erro no Envio' : 'Enviando...'}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 py-4 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">{sendProgress.sent}/{sendProgress.total} enviados</span>
              <span className="text-xs text-slate-400">{sendProgress.total > 0 ? Math.round(sendProgress.sent / sendProgress.total * 100) : 0}%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div className="bg-green-500 h-2 rounded-full transition-all duration-300" style={{ width: `${sendProgress.total > 0 ? (sendProgress.sent / sendProgress.total * 100) : 0}%` }} />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-3 space-y-1" data-testid="send-progress-logs">
            {sendProgress.logs.map((log, i) => (
              <div key={i} className={`flex items-center gap-2 text-xs py-1 ${log.type === 'sent' ? 'text-green-700' : log.type === 'error' ? 'text-red-600' : log.type === 'pausing' ? 'text-amber-600' : 'text-slate-500'}`}>
                {log.type === 'sent' && <CheckCircle className="w-3 h-3 flex-shrink-0" />}
                {log.type === 'error' && <XCircle className="w-3 h-3 flex-shrink-0" />}
                {log.type === 'pausing' && <Clock className="w-3 h-3 flex-shrink-0" />}
                <span>
                  {log.type === 'sent' && `${log.nome} (***${log.number})`}
                  {log.type === 'error' && `${log.nome} — ${log.reason}`}
                  {log.type === 'pausing' && `Pausando ${log.duration}s...`}
                  {log.type === 'complete' && `Envio concluído: ${log.sent}/${log.total}`}
                  {log.type === 'cancelled' && `Envio cancelado: ${log.sent}/${log.total}`}
                </span>
              </div>
            ))}
            {sendProgress.logs.length === 0 && sendProgress.status === 'sending' && (
              <div className="flex items-center gap-2 text-sm text-slate-400 py-4"><Loader2 className="w-4 h-4 animate-spin" />Iniciando envio...</div>
            )}
          </div>
          <div className="px-6 py-3 border-t border-slate-100 flex items-center flex-shrink-0" style={{ justifyContent: sendProgress.status === 'sending' ? 'flex-start' : 'flex-end' }}>
            {sendProgress.status === 'sending' ? (
              <Button size="sm" onClick={() => setPauseConfirm(true)} className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                <Pause className="w-4 h-4 mr-1.5" />Pausar Envio
              </Button>
            ) : (
              <Button size="sm" className="w-full bg-green-600 hover:bg-green-700" onClick={() => { setSendingCampaignId(null); setSendProgress({ sent: 0, total: 0, logs: [], status: null }); }}>
                Fechar
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Associar Telefone */}
      <Dialog open={!!assocModal && !assocConfirm && !quickAddOpen} onOpenChange={(v) => { if (!v) setAssocModal(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0 sm:p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100 flex-shrink-0">
            <DialogTitle className="text-base">Associar Número a Pessoa</DialogTitle>
          </DialogHeader>
          <div className="px-5 py-3 space-y-3 flex-1 min-h-0 flex flex-col">
            <div className="bg-slate-50 rounded-lg px-3 py-2 text-sm flex-shrink-0">
              <div className="space-y-0.5">
                <p><span className="text-slate-400">Nome:</span> <span className="font-medium text-slate-800">{assocModal?.nome || 'Sem nome'}</span></p>
                <p><span className="text-slate-400">WhatsApp:</span> <span className="font-medium text-slate-800">{(() => { const ph = assocModal?.phone || ''; let d = ph.startsWith('55') && ph.length > 10 ? ph.slice(2) : ph; if (d.length === 10) d = d.slice(0,2) + '9' + d.slice(2); return d.length === 11 ? `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}` : `+${ph}`; })()}</span></p>
              </div>
            </div>
            <div className="flex items-center justify-between flex-shrink-0">
              <Label className="text-xs text-slate-500">Buscar pessoa pelo nome</Label>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={() => openInvite(assocModal?.phone, assocModal?.nome)}>
                  <Send className="w-3 h-3" />Convidar
                </Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={openQuickAdd}>
                  <Plus className="w-3 h-3" />Pessoa
                </Button>
              </div>
            </div>
            <div className="relative flex-shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
              <Input value={assocSearch} onChange={e => setAssocSearch(e.target.value)} placeholder="Digite o nome..." className="pl-8 h-9 text-sm" data-testid="assoc-search" autoFocus />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {assocPessoas.filter(p => !assocSearch || (p.nome_completo || '').toLowerCase().includes(assocSearch.toLowerCase())).slice(0, 50).map(p => (
                <button key={p.pessoa_id} onClick={() => setAssocConfirm({ pessoaId: p.pessoa_id, pessoaNome: p.nome_completo, pessoaWhatsapp: p.whatsapp || '' })} disabled={assocSaving}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-left hover:bg-green-50 transition-colors border-b border-slate-50 last:border-b-0"
                  data-testid={`assoc-pessoa-${p.pessoa_id}`}>
                  <span className="font-medium text-slate-800 flex-1 truncate">{p.nome_completo}</span>
                  {p.whatsapp && <span className="text-[10px] text-slate-400 flex-shrink-0">{p.whatsapp}</span>}
                  <span className="text-[10px] text-green-600 font-medium flex-shrink-0">Associar</span>
                </button>
              ))}
              {assocPessoas.length === 0 && <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" /></div>}
              {assocPessoas.length > 0 && assocSearch && assocPessoas.filter(p => (p.nome_completo || '').toLowerCase().includes(assocSearch.toLowerCase())).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-3">Nenhuma pessoa encontrada</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Cadastro Rápido de Pessoa */}
      <Dialog open={quickAddOpen} onOpenChange={(v) => { if (!v) setQuickAddOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Nova Pessoa</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500">Nome Completo *</Label>
              <Input value={quickAddForm.nome_completo} onChange={e => setQuickAddForm(prev => ({ ...prev, nome_completo: e.target.value }))} data-testid="quick-add-nome" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Gênero *</Label>
                <Select value={quickAddForm.genero} onValueChange={v => setQuickAddForm(prev => ({ ...prev, genero: v }))}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feminino">Feminino</SelectItem>
                    <SelectItem value="masculino">Masculino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">WhatsApp</Label>
                <Input value={assocModal?.formattedPhone || ''} readOnly className="bg-slate-50 text-slate-600" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button size="sm" variant="outline" onClick={() => setQuickAddOpen(false)}>Cancelar</Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleQuickAdd} disabled={quickAddSaving}>
                {quickAddSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                Cadastrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Convidar para Cadastro */}
      <Dialog open={!!inviteModal} onOpenChange={(v) => { if (!v) setInviteModal(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Send className="w-4 h-4 text-green-600" />Convidar para Cadastro</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-lg px-3 py-2 text-sm space-y-0.5">
              <p><span className="text-slate-400">Para:</span> <span className="font-medium text-slate-800">{inviteModal?.nome || `+${inviteModal?.phone}`}</span></p>
              <p><span className="text-slate-400">WhatsApp:</span> <span className="font-medium text-slate-800">{(() => { const ph = inviteModal?.phone || ''; let d = ph.startsWith('55') && ph.length > 10 ? ph.slice(2) : ph; if (d.length === 10) d = d.slice(0,2) + '9' + d.slice(2); return d.length === 11 ? `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}` : `+${ph}`; })()}</span></p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Mensagem</Label>
              <textarea value={inviteMsg} onChange={e => setInviteMsg(e.target.value)} rows={8} className="w-full mt-1 px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setInviteModal(null)}>Cancelar</Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={handleSendInvite} disabled={inviteSending}>
                {inviteSending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Enviar Convite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Confirmação de Associação */}
      <AlertDialog open={!!assocConfirm} onOpenChange={(v) => { if (!v) setAssocConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Associação</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>Você está alterando o contato de <strong className="text-slate-800">{assocConfirm?.pessoaNome}</strong>:</p>
                <div className="bg-slate-50 rounded-lg px-3 py-2 space-y-1">
                  <p><span className="text-slate-400">De:</span> <strong>{assocConfirm?.pessoaWhatsapp || 'Sem número'}</strong></p>
                  <p><span className="text-slate-400">Para:</span> <strong>{(() => { const d = (assocModal?.phone || '').startsWith('55') && (assocModal?.phone || '').length > 10 ? (assocModal?.phone || '').slice(2) : (assocModal?.phone || ''); return d.length === 11 ? `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}` : `+${assocModal?.phone}`; })()}</strong>
                  {assocModal?.nome && <span className="text-slate-400 ml-1">({assocModal.nome})</span>}</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAssocConfirm(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleAssociate} disabled={assocSaving} className="bg-green-600 hover:bg-green-700">
              {assocSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
